import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import {
  LayoutDashboard,
  BookOpen,
  Users,
  CreditCard,
  Mail,
  Settings,
  LogOut,
  Menu,
  X,
  GraduationCap,
  Bell,
  ChevronRight,
  Shield,
  Clock,
  ShieldAlert,
  LifeBuoy,
  DollarSign,
  Wallet,
  FileText,
  ScrollText,
  ChevronDown,
  Command,
  ArrowLeft,
  Globe,
  Home,
  Flag,
  HelpCircle,
  Zap,
  FolderGit2,
  Target,
  Tags,
  UserCircle,
  MessageSquareQuote,
  Scale,
  PanelBottom,
  Info,
  Phone,
  LogIn,
  Palette,
  BarChart3,
  ShoppingCart,
  Award,
} from 'lucide-react';

interface NavItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface NavGroup {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  bgColor: string;
  borderColor: string;
  textColor: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    id: 'overview',
    label: 'Overview',
    icon: LayoutDashboard,
    color: 'amber',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    textColor: 'text-amber-600',
    items: [{ path: '/admin', label: 'Dashboard', icon: LayoutDashboard }],
  },
  {
    id: 'academy',
    label: 'Academy',
    icon: BookOpen,
    color: 'purple',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    textColor: 'text-purple-600',
    items: [
      { path: '/admin/courses', label: 'Courses', icon: BookOpen },
      { path: '/admin/workshops', label: 'Workshops', icon: Clock },
      { path: '/admin/blogs', label: 'Blogs', icon: FileText },
      { path: '/admin/enrollments', label: 'Enrollments', icon: GraduationCap },
      { path: '/admin/certificates', label: 'Certificates', icon: Award },
    ],
  },
  {
    id: 'community',
    label: 'Community',
    icon: Users,
    color: 'blue',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    textColor: 'text-blue-600',
    items: [
      { path: '/admin/users', label: 'Users', icon: Users },
      { path: '/admin/moderation', label: 'Moderation', icon: ShieldAlert },
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    icon: CreditCard,
    color: 'emerald',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    textColor: 'text-emerald-600',
    items: [
      { path: '/admin/payments', label: 'Payments', icon: CreditCard },
      { path: '/admin/payouts', label: 'Payouts', icon: DollarSign },
    ],
  },
  {
    id: 'referrals',
    label: 'Referrals',
    icon: Users,
    color: 'purple',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    textColor: 'text-purple-600',
    items: [
      { path: '/admin/referrals', label: 'All Referrals', icon: Users },
      { path: '/admin/referral-earnings', label: 'Earnings', icon: DollarSign },
      { path: '/admin/referral-payouts', label: 'Payouts', icon: Wallet },
      { path: '/admin/referral-applications', label: 'Applications', icon: FileText },
    ],
  },
  {
    id: 'marketing',
    label: 'Marketing',
    icon: Mail,
    color: 'rose',
    bgColor: 'bg-rose-50',
    borderColor: 'border-rose-200',
    textColor: 'text-rose-600',
    items: [
      { path: '/admin/marketing', label: 'Analytics', icon: BarChart3 },
      { path: '/admin/leads', label: 'Leads', icon: Mail },
      { path: '/admin/abandoned-carts', label: 'Abandoned Carts', icon: ShoppingCart },
      { path: '/admin/whatsapp', label: 'WhatsApp', icon: Phone },
      { path: '/admin/email', label: 'Email', icon: Mail },
    ],
  },
  {
    id: 'cms',
    label: 'CMS',
    icon: FileText,
    color: 'orange',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    textColor: 'text-orange-600',
    items: [
      { path: '/admin/cms#general', label: 'General', icon: Globe },
      { path: '/admin/cms#homepage', label: 'Homepage', icon: Home },
      { path: '/admin/cms#landing_hero', label: 'Landing — Hero', icon: Flag },
      { path: '/admin/cms#landing_social', label: 'Landing — Social', icon: Users },
      { path: '/admin/cms#landing_problem', label: 'Landing — Problem', icon: HelpCircle },
      { path: '/admin/cms#landing_highlights', label: 'Landing — Highlights', icon: Zap },
      { path: '/admin/cms#landing_curriculum', label: 'Landing — Curriculum', icon: BookOpen },
      { path: '/admin/cms#landing_projects', label: 'Landing — Projects', icon: FolderGit2 },
      { path: '/admin/cms#landing_audience', label: 'Landing — Audience', icon: Target },
      { path: '/admin/cms#landing_pricing', label: 'Landing — Pricing', icon: Tags },
      { path: '/admin/cms#landing_instructor', label: 'Landing — Instructor', icon: UserCircle },
      { path: '/admin/cms#landing_testimonials', label: 'Landing — Testimonials', icon: MessageSquareQuote },
      { path: '/admin/cms#landing_comparison', label: 'Landing — Comparison', icon: Scale },
      { path: '/admin/cms#landing_faq', label: 'Landing — FAQ', icon: HelpCircle },
      { path: '/admin/cms#landing_enroll', label: 'Landing — Enroll CTA', icon: Zap },
      { path: '/admin/cms#landing_footer', label: 'Landing — Footer', icon: PanelBottom },
      { path: '/admin/cms#about', label: 'About Page', icon: Info },
      { path: '/admin/cms#contact', label: 'Contact Page', icon: Phone },
      { path: '/admin/cms#login', label: 'Login Page', icon: LogIn },
      { path: '/admin/cms#theme', label: 'Theme', icon: Palette },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    icon: LifeBuoy,
    color: 'cyan',
    bgColor: 'bg-cyan-50',
    borderColor: 'border-cyan-200',
    textColor: 'text-cyan-600',
    items: [
      { path: '/admin/support', label: 'Support', icon: LifeBuoy },
      { path: '/admin/logs', label: 'Logs', icon: ScrollText },
    ],
  },
  {
    id: 'system',
    label: 'System',
    icon: Settings,
    color: 'slate',
    bgColor: 'bg-slate-50',
    borderColor: 'border-slate-200',
    textColor: 'text-slate-600',
    items: [{ path: '/admin/settings', label: 'Settings', icon: Settings }],
  },
];

function getBasePath(path: string): string {
  return path.split('#')[0];
}

function getActiveGroup(pathname: string): string {
  if (pathname === '/admin') return 'overview';
  for (const group of navGroups) {
    for (const item of group.items) {
      const basePath = getBasePath(item.path);
      if (basePath !== '/admin' && pathname.startsWith(basePath)) {
        return group.id;
      }
    }
  }
  return 'overview';
}

function isItemActive(path: string, pathname: string, hash: string): boolean {
  if (path === '/admin') return pathname === '/admin';
  const basePath = getBasePath(path);
  if (!pathname.startsWith(basePath)) return false;
  const expectedHash = path.includes('#') ? path.split('#')[1] : '';
  if (expectedHash) return hash === `#${expectedHash}`;
  return true;
}

export default function AdminLayout({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { user, clearAuth } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    const active = getActiveGroup(location.pathname);
    return { [active]: true };
  });

  useEffect(() => {
    const active = getActiveGroup(location.pathname);
    setExpandedGroups((prev) => ({ ...prev, [active]: true }));
  }, [location.pathname]);

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const activeGroupId = getActiveGroup(location.pathname);

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-72 bg-white border-r border-slate-200 fixed h-full z-30">
        {/* Logo */}
        <div className="p-5 border-b border-slate-100">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-amber-500 flex items-center justify-center shadow-sm shadow-amber-500/20">
              <Command className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="block text-sm font-bold text-slate-900">EcoleMinds</span>
              <span className="block text-[10px] text-slate-400 font-medium uppercase tracking-wider">Admin Panel</span>
            </div>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-3 overflow-y-auto">
          <p className="px-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
            Main Menu
          </p>
          {navGroups.map((group) => {
            const Icon = group.icon;
            const isExpanded = expandedGroups[group.id];
            const isActiveGroup = activeGroupId === group.id;
            const hasSubItems = group.items.length > 1;

            return (
              <div
                key={group.id}
                className={`rounded-2xl border transition-all duration-200 ${
                  isActiveGroup
                    ? `${group.bgColor} ${group.borderColor} shadow-sm`
                    : 'bg-white border-slate-100 hover:border-slate-200 hover:shadow-sm'
                }`}
              >
                {/* Group Header */}
                <button
                  onClick={() => {
                    if (hasSubItems) {
                      toggleGroup(group.id);
                    } else {
                      navigate(group.items[0].path);
                    }
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-semibold transition-colors ${
                    isActiveGroup ? group.textColor : 'text-slate-700 hover:text-slate-900'
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-xl flex items-center justify-center ${
                      isActiveGroup ? 'bg-white/80 shadow-sm' : 'bg-slate-100'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                  </div>
                  <span className="flex-1 text-left">{group.label}</span>
                  {hasSubItems && (
                    <motion.div
                      animate={{ rotate: isExpanded ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ChevronDown className="w-4 h-4 opacity-60" />
                    </motion.div>
                  )}
                  {!hasSubItems && isActiveGroup && (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>

                {/* Sub Items */}
                <AnimatePresence initial={false}>
                  {hasSubItems && isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <div className="px-3 pb-3 space-y-1">
                        {group.items.map((item) => {
                          const ItemIcon = item.icon;
                          const active = isItemActive(item.path, location.pathname, location.hash);
                          return (
                            <Link
                              key={item.path}
                              to={item.path}
                              className={`flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                                active
                                  ? 'bg-white text-slate-900 shadow-sm'
                                  : 'text-slate-500 hover:text-slate-700 hover:bg-white/60'
                              }`}
                            >
                              <ItemIcon className={`w-3.5 h-3.5 ${active ? group.textColor : ''}`} />
                              {item.label}
                              {active && (
                                <div className={`w-1.5 h-1.5 rounded-full ml-auto ${group.textColor.replace('text-', 'bg-')}`} />
                              )}
                            </Link>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </nav>

        {/* User Profile */}
        <div className="p-4 border-t border-slate-100">
          <div className="flex items-center gap-3 mb-3 p-2 rounded-xl hover:bg-slate-50 transition-colors">
            {user?.avatar ? (
              <img src={user.avatar} alt={user.name} className="w-9 h-9 rounded-full object-cover ring-2 ring-slate-100" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-amber-500 flex items-center justify-center text-white text-xs font-bold ring-2 ring-amber-100">
                {user?.name?.charAt(0)?.toUpperCase() || 'A'}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-slate-900 truncate">{user?.name || 'Admin'}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={() => { clearAuth(); navigate('/'); }}
            className="flex items-center gap-2 w-full px-3 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50 rounded-xl transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSidebarOpen(false)}
              className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm"
            />
            <motion.aside
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed left-0 top-0 bottom-0 w-72 bg-white border-r border-slate-200 z-50 lg:hidden flex flex-col"
            >
              <div className="p-5 border-b border-slate-100 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-amber-500 flex items-center justify-center">
                    <Command className="w-5 h-5 text-white" />
                  </div>
                  <span className="font-bold text-slate-900 text-sm">EcoleMinds</span>
                </Link>
                <button onClick={() => setSidebarOpen(false)} className="p-1.5 rounded-lg hover:bg-slate-100">
                  <X className="w-5 h-5 text-slate-600" />
                </button>
              </div>

              <nav className="flex-1 p-4 space-y-3 overflow-y-auto">
                <p className="px-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
                  Main Menu
                </p>
                {navGroups.map((group) => {
                  const Icon = group.icon;
                  const isExpanded = expandedGroups[group.id];
                  const isActiveGroup = activeGroupId === group.id;
                  const hasSubItems = group.items.length > 1;

                  return (
                    <div
                      key={group.id}
                      className={`rounded-2xl border transition-all duration-200 ${
                        isActiveGroup
                          ? `${group.bgColor} ${group.borderColor} shadow-sm`
                          : 'bg-white border-slate-100'
                      }`}
                    >
                      <button
                        onClick={() => {
                          if (hasSubItems) {
                            toggleGroup(group.id);
                          } else {
                            navigate(group.items[0].path);
                            setSidebarOpen(false);
                          }
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-semibold transition-colors ${
                          isActiveGroup ? group.textColor : 'text-slate-700'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${isActiveGroup ? 'bg-white/80' : 'bg-slate-100'}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <span className="flex-1 text-left">{group.label}</span>
                        {hasSubItems && (
                          <motion.div animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.2 }}>
                            <ChevronDown className="w-4 h-4 opacity-60" />
                          </motion.div>
                        )}
                      </button>

                      <AnimatePresence initial={false}>
                        {hasSubItems && isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            <div className="px-3 pb-3 space-y-1">
                              {group.items.map((item) => {
                                const ItemIcon = item.icon;
                                const active = isItemActive(item.path, location.pathname, location.hash);
                                return (
                                  <Link
                                    key={item.path}
                                    to={item.path}
                                    onClick={() => setSidebarOpen(false)}
                                    className={`flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                                      active
                                        ? 'bg-white text-slate-900 shadow-sm'
                                        : 'text-slate-500 hover:text-slate-700 hover:bg-white/60'
                                    }`}
                                  >
                                    <ItemIcon className={`w-3.5 h-3.5 ${active ? group.textColor : ''}`} />
                                    {item.label}
                                    {active && (
                                      <div className={`w-1.5 h-1.5 rounded-full ml-auto ${group.textColor.replace('text-', 'bg-')}`} />
                                    )}
                                  </Link>
                                );
                              })}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </nav>

              <div className="p-4 border-t border-slate-100">
                <button
                  onClick={() => { clearAuth(); navigate('/'); }}
                  className="flex items-center gap-2 w-full px-3 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                >
                  <LogOut className="w-4 h-4" /> Sign Out
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="flex-1 lg:ml-72">
        {/* Mobile Header */}
        <header className="lg:hidden bg-white/80 backdrop-blur-md border-b border-slate-200 px-4 py-3 flex items-center justify-between sticky top-0 z-20">
          <button onClick={() => setSidebarOpen(true)} className="p-2 rounded-xl hover:bg-slate-100">
            <Menu className="w-5 h-5 text-slate-600" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-amber-500 flex items-center justify-center">
              <Command className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-slate-900 text-sm">Admin</span>
          </div>
          <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center text-white text-xs font-bold">
            {user?.name?.charAt(0)?.toUpperCase() || 'A'}
          </div>
        </header>

        {/* Desktop Header */}
        <header className="hidden lg:flex items-center justify-between bg-white/80 backdrop-blur-md border-b border-slate-200 px-6 py-3 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            {location.pathname !== '/admin' && (
              <button
                onClick={() => navigate('/admin')}
                className="p-2 rounded-xl hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors"
                title="Back to Dashboard"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Shield className="w-4 h-4 text-amber-500" />
              <span className="font-medium">Admin Dashboard</span>
              {location.pathname !== '/admin' && (
                <>
                  <ChevronRight className="w-3 h-3 text-slate-300" />
                  <span className="text-slate-900 font-semibold capitalize">
                    {location.pathname.split('/').pop()?.replace(/-/g, ' ')}
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/notifications" className="relative p-2 rounded-xl hover:bg-slate-100 transition-colors">
              <Bell className="w-5 h-5 text-slate-500" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full ring-2 ring-white" />
            </Link>
            <Link
              to="/"
              className="text-sm font-medium text-slate-600 hover:text-amber-600 transition-colors px-3 py-2 rounded-xl hover:bg-amber-50"
            >
              View Site
            </Link>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
